/**
 * apps/web/src/main.tsx
 *
 * Entry point. Provider order (outer → inner):
 *   QueryClientProvider  - global server-state cache
 *   BrowserRouter        - routing
 *   AuthProvider         - session/identity (reads /auth/me on boot)
 *   LanguageProvider     - active UI language (reads workspace.locale)
 *   App                  - route table
 *
 * AuthProvider sits under the router so it can use router-aware redirects later,
 * and under React Query so any future query-based auth work shares the cache.
 * LanguageProvider sits under AuthProvider so it can default to the workspace
 * locale once the session loads.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { AuthProvider } from './auth/AuthContext';
import { LanguageProvider } from './i18n';
import { queryClient } from './lib/queryClient';
import { ErrorBoundary } from './components/ErrorBoundary';
import { OfflineCache } from './components/OfflineCache';
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found in index.html');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Last-resort boundary: a crash anywhere (incl. a stale lazy chunk after a
          deploy) shows a recoverable screen instead of a white page. */}
      <ErrorBoundary boundaryName="app-root" variant="page">
        <BrowserRouter>
          <AuthProvider>
            <LanguageProvider>
              <OfflineCache>
                <App />
              </OfflineCache>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </QueryClientProvider>
  </React.StrictMode>,
);
